import { Card, Title, Text, Group, Badge, Stack, Button } from "@mantine/core";
import { IconEdit, IconDownload } from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AGENDA_RETURN_FORM_FIELD_LABELS,
  agendaReturnFormProcurement,
  formatDhivehiDate,
  formatReturnFormBudget,
  isChairpersonVoteIncluded,
} from "@hmh-proc/shared";
import { RoleGuard } from "../components/RoleGuard";
import {
  DhivehiText,
  FormFieldsTable,
  FormTitle,
  LtrText,
  MemberVotesTable,
  MyApprovalsHint,
  OfficerBox,
  membersListedOnForm,
  type FormFieldRow,
} from "../components/DhivehiFormSections";
import {
  useAgendaReturnForm,
  useSendAgendaReturnFormForApproval,
  useGenerateAgendaReturnFormPdf,
  useChairApproveAgendaReturnForm,
  useSecretarySignAgendaReturnForm,
} from "../hooks";
import { useAuth } from "../../../auth/AuthContext";
import { approvalDisplayName, singleSourceRtlLayoutStyle } from "../../../utils/dhivehiText";

const L = AGENDA_RETURN_FORM_FIELD_LABELS;
const officerLabels = { name: L.nameLabel, signature: L.signatureLabel, rejected: L.fail };

export function AgendaReturnFormDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: form, isLoading, error } = useAgendaReturnForm(id);
  const sendForApproval = useSendAgendaReturnFormForApproval();
  const generatePdf = useGenerateAgendaReturnFormPdf();
  const chairApprove = useChairApproveAgendaReturnForm();
  const secretarySign = useSecretarySignAgendaReturnForm();

  if (isLoading) {
    return <Text>Loading...</Text>;
  }
  if (error || !form) {
    return <Text c="red">Failed to load agenda return form</Text>;
  }

  const refreshForm = () => queryClient.invalidateQueries({ queryKey: ["agendaReturnForm", id] });

  const attendances = form.meeting?.attendances ?? [];
  const myAttendance = attendances.find((a) => a.memberId === user?.id);
  const chairAttendance = attendances.find((a) => a.isChairperson);
  const secretaryAttendance = attendances.find((a) => a.isSecretary);
  const approvals = form.approvals ?? [];
  const myApproval = approvals.find((a) => a.memberId === user?.id);
  const approvalOf = (memberId?: string) => approvals.find((a) => a.memberId === memberId);

  const members = membersListedOnForm(approvals, {
    chairId: chairAttendance?.memberId,
    secretaryId: secretaryAttendance?.memberId,
    chairVotes: isChairpersonVoteIncluded(attendances),
  });

  const procurement = agendaReturnFormProcurement(form);
  const rows: FormFieldRow[] = [
    {
      label: L.meetingDate,
      value: <DhivehiText value={form.meeting?.plannedStart ? formatDhivehiDate(form.meeting.plannedStart) : null} />,
    },
    { label: L.meetingNumber, value: <LtrText value={form.meeting?.meetingNumber} /> },
    { label: L.procurementName, value: <DhivehiText value={procurement.name} /> },
    { label: L.procurementNumber, value: <LtrText value={procurement.number} /> },
    { label: L.budget, value: <DhivehiText value={formatReturnFormBudget(procurement.budget)} /> },
    { label: L.reasonsForReturning, value: <DhivehiText value={form.reasonsForReturning} multiline /> },
    { label: L.detailsOfAmendments, value: <DhivehiText value={form.detailsOfAmendmentsRequired} multiline /> },
    { label: L.resubmission, value: <DhivehiText value={form.allowedToResubmitWithAmendments} multiline /> },
  ];

  const statusColor: Record<string, string> = {
    DRAFT: "gray",
    PENDING: "yellow",
    APPROVED: "green",
    REJECTED: "red",
  };

  return (
    <Stack gap="lg">
      <Card>
        <Group justify="space-between">
          <div>
            <Title order={2}>{form.formNumber ?? "Agenda return form"}</Title>
            <Text c="dimmed" size="sm">
              {form.meeting?.title}
            </Text>
          </div>
          <Group>
            <Badge color={statusColor[form.status] || "gray"} size="lg">
              {form.status}
            </Badge>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={() => generatePdf.mutate(form.id)}
              loading={generatePdf.isPending}
            >
              Download PDF
            </Button>
            {form.status === "DRAFT" && (
              <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}>
                <Button
                  variant="outline"
                  leftSection={<IconEdit size={16} />}
                  onClick={() => navigate(`/bid-committee/agenda-return-forms/${form.id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  onClick={() => sendForApproval.mutate(form.id, { onSuccess: refreshForm })}
                  loading={sendForApproval.isPending}
                >
                  Send for approval
                </Button>
              </RoleGuard>
            )}
          </Group>
        </Group>
      </Card>

      {form.status === "PENDING" && myApproval?.status === "PENDING" && <MyApprovalsHint what="return form" />}

      <Card style={singleSourceRtlLayoutStyle()}>
        <Stack gap="lg">
          <FormTitle title={L.title} number={form.formNumber} />

          <FormFieldsTable rows={rows} />

          <MemberVotesTable
            heading={L.acknowledgement}
            text={L.acknowledgementText}
            columns={{ name: L.colMemberName, office: L.colOffice, approve: L.colPass, reject: L.colFail }}
            marks={{ approve: L.pass, reject: L.fail }}
            votes={members}
          />

          <OfficerBox
            title={L.chairPerson}
            labels={officerLabels}
            name={approvalDisplayName(form.chairApprovedBy ?? chairAttendance?.member)}
            signed={!!form.chairApprovedAt}
            signedAt={form.chairApprovedAt}
            rejected={approvalOf(chairAttendance?.memberId)?.status === "REJECTED"}
            action={
              form.status === "PENDING" &&
              myAttendance?.isChairperson &&
              !form.chairApprovedAt && (
                <Button
                  size="xs"
                  onClick={() => chairApprove.mutate({ id: form.id }, { onSuccess: refreshForm })}
                  loading={chairApprove.isPending}
                >
                  Approve as chair
                </Button>
              )
            }
          />

          <OfficerBox
            title={L.secretary}
            labels={officerLabels}
            name={approvalDisplayName(form.secretarySignedBy ?? secretaryAttendance?.member)}
            signed={!!form.secretarySignedAt}
            signedAt={form.secretarySignedAt}
            rejected={approvalOf(secretaryAttendance?.memberId)?.status === "REJECTED"}
            action={
              form.status === "PENDING" &&
              myAttendance?.isSecretary &&
              !form.secretarySignedAt && (
                <Button
                  size="xs"
                  onClick={() => secretarySign.mutate({ id: form.id }, { onSuccess: refreshForm })}
                  loading={secretarySign.isPending}
                >
                  Sign as secretary
                </Button>
              )
            }
          />
        </Stack>
      </Card>
    </Stack>
  );
}
